/**
 * Settled table pages kept for the life of one dashboard instance, so a
 * table that remounts (a tab switch) paints its rows at once instead of
 * a loading body, and the tables of the other tabs can be fetched ahead.
 *
 * Correctness: a hit is the answer to the SAME request (table, filters,
 * sort, page size, offset, search) under the same data source, never a
 * neighbour's. The table still revalidates on every mount, so a data
 * reload while the page is open lands on the next fetch — the cached
 * page is only what is painted while that fetch is in flight.
 */

import type { DashboardDataSource, FetchTableDataArgs } from "./dashboard-data";
import type { TableData } from "./types";

/** Stable identity of a table request: same args → same key, whatever
 *  the insertion order of the filters. An empty value stays in the key:
 *  for the engine "" (cleared) and an absent key (its default) differ. */
export function tableDataKey(args: FetchTableDataArgs): string {
  const filters = Object.entries(args.filters).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
  return JSON.stringify([
    args.tableId,
    filters,
    args.sortBy ?? null,
    args.sortDir ?? null,
    args.limit,
    args.offset,
    args.search ?? null,
  ]);
}

/** `source` with `fetchTableData` remembering each settled page and a
 *  synchronous `peekTableData` to read it back. Every other method is
 *  the source's own. Failed fetches are not stored. */
export function withTableDataCache(source: DashboardDataSource): DashboardDataSource {
  const pages = new Map<string, TableData>();
  return {
    ...source,
    fetchTableData: async (args) => {
      const page = await source.fetchTableData(args);
      pages.set(tableDataKey(args), page);
      return page;
    },
    peekTableData: (args) => pages.get(tableDataKey(args)),
  };
}
